import type { VehicleType } from '../types';

export type PinType = 'spot' | 'seeker' | 'rent';
export type ViewMode = 'all' | 'spots' | 'seekers' | 'rent';
export type LocationSource = 'gps' | 'ip';
export type LocationOutcome = 'success' | 'denied' | 'outside_india' | 'error';
export type FilterKey =
  | 'vehicle_type'
  | 'parking_type'
  | 'duration'
  | 'min_price'
  | 'max_price'
  | 'covered'
  | 'security'
  | 'access_24x7'
  | 'ev'
  | 'free';
export type MarketingPageKind = 'home' | 'city' | 'locality' | 'use_case' | 'guide' | 'comparison' | 'mission' | 'price_report' | 'static';
export type MarketingCtaName = 'list_spot' | 'find_parking' | 'pin_rent' | 'view_stats' | 'read_guide' | 'open_map';
export type OutboundDestination = 'competitor' | 'youtube' | 'reddit' | 'quora' | 'google_business' | 'other';

export interface AnalyticsEvents {
  page_view: {
    page_path: string;
    page_title?: string;
    page_referrer?: string;
  };
  map_loaded: {
    provider: 'olamaps';
    result: 'success' | 'error';
    error_kind?: string;
  };
  map_popup_open: {
    pin_type: PinType;
  };
  map_click_to_drop: {
    form_mode: PinType;
  };
  location_detect_attempt: {
    source: LocationSource;
  };
  location_detect_outcome: {
    source: LocationSource;
    result: LocationOutcome;
  };
  search_performed: {
    result_count: number;
  };
  search_result_selected: {
    result_type?: string;
  };
  drawer_open: Record<string, never>;
  legend_toggle: {
    state: 'open' | 'closed';
  };
  view_mode_change: {
    from: ViewMode;
    to: ViewMode;
  };
  fab_opened: Record<string, never>;
  add_pin_started: {
    pin_type: PinType;
  };
  add_pin_cancelled: {
    pin_type: PinType;
    step: 'before_pin_drop' | 'after_pin_drop';
  };
  add_pin_validation_error: {
    pin_type: PinType;
    error_field: string;
  };
  form_submit_attempt: {
    pin_type: PinType;
  };
  form_submit_success: {
    pin_type: PinType;
  };
  form_submit_failure: {
    pin_type: PinType;
    error_code: 'validation' | 'rate_limit' | 'network' | 'server' | 'unknown';
  };
  filter_changed: {
    filter_key: FilterKey;
    value_category: string;
  };
  filters_cleared: {
    active_count_before: number;
  };
  contact_form_submitted: {
    has_contact: boolean;
    result: 'success' | 'failure';
  };
  share_clicked: {
    pin_type: PinType;
    share_method: 'copy_link' | 'whatsapp';
  };
  share_link_open: {
    pin_type: PinType;
  };
  pin_report_attempt: {
    pin_type: Exclude<PinType, 'seeker'>;
    reason_category:
      | 'inaccurate_location'
      | 'wrong_info'
      | 'duplicate'
      | 'not_parking'
      | 'spam'
      | 'other';
  };
  pin_reported: {
    pin_type: Exclude<PinType, 'seeker'>;
    reason_category:
      | 'inaccurate_location'
      | 'wrong_info'
      | 'duplicate'
      | 'not_parking'
      | 'spam'
      | 'other';
    result:
      | 'success'
      | 'duplicate'
      | 'rate_limited'
      | 'validation'
      | 'network'
      | 'server'
      | 'unknown';
  };
  interest_form_opened: {
    pin_type: 'spot';
  };
  interest_form_submitted: {
    pin_type: 'spot';
    has_message: boolean;
    has_name: boolean;
  };
  interest_form_result: {
    pin_type: 'spot';
    result: 'success' | 'duplicate' | 'rate_limited' | 'validation' | 'network' | 'server' | 'unknown';
  };
  comment_thread_opened: {
    pin_type: PinType;
    comment_count: number;
  };
  comment_form_submitted: {
    pin_type: PinType;
    has_display_name: boolean;
    body_length_bucket: 'short' | 'medium' | 'long';
  };
  comment_form_result: {
    pin_type: PinType;
    result: 'success' | 'rate_limited' | 'validation' | 'network' | 'server' | 'unknown';
  };
  comment_reported: {
    pin_type: PinType;
    result: 'success' | 'rate_limited' | 'self_report' | 'network' | 'server' | 'unknown';
  };
  app_error: {
    surface: string;
    message_kind: string;
  };
  tour_started: Record<string, never>;
  tour_step_completed: {
    step_id: string;
  };
  tour_step_skipped: {
    step_id: string;
  };
  tour_completed: {
    step_count: number;
  };
  tour_dismissed: {
    at_step_id: string;
    step_index: number;
  };
  help_tip_opened: {
    tip_key: string;
  };
  marketing_cta_clicked: {
    page_kind: MarketingPageKind;
    surface: 'hero' | 'inline' | 'footer' | 'sidebar';
    cta: MarketingCtaName;
  };
  marketing_outbound_click: {
    destination: OutboundDestination;
    target_kind: 'video' | 'post' | 'profile' | 'article';
    surface: MarketingPageKind;
  };
  marketing_whatsapp_share: {
    page_kind: MarketingPageKind;
    surface: 'inline' | 'floating' | 'result';
  };
  price_snapshot_view: {
    scope: 'city' | 'locality';
    city: string;
    locality?: string;
    vehicle_type?: VehicleType;
  };
  city_quickzoom: {
    city: string;
    source: 'home_grid' | 'city_page' | 'search';
  };
}

const PII_KEYS = new Set([
  'email',
  'phone',
  'query',
  'search_query',
  'message',
  'contact',
  'lat',
  'lng',
  'latitude',
  'longitude',
  'building_name',
  'description',
]);

function sanitize(params: Record<string, unknown> | undefined): Record<string, unknown> {
  if (!params) return {};
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (PII_KEYS.has(key)) continue;
    if (typeof value === 'string' && value.length > 200) {
      out[key] = value.slice(0, 200);
    } else {
      out[key] = value;
    }
  }
  return out;
}

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

export function trackEvent<E extends keyof AnalyticsEvents>(
  name: E,
  params: AnalyticsEvents[E]
): void {
  if (import.meta.env.DEV) return;
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('event', name, sanitize(params as Record<string, unknown>));
}

export function trackPageView(path: string, title?: string): void {
  if (import.meta.env.DEV) return;
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title ?? document.title,
    page_referrer: document.referrer || undefined,
  });
}

export function mapApiErrorToCode(err: unknown): 'validation' | 'rate_limit' | 'network' | 'server' | 'unknown' {
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    if (msg.includes('rate') || msg.includes('limit') || msg.includes('too many')) return 'rate_limit';
    if (msg.includes('network') || msg.includes('failed to fetch') || msg.includes('timeout')) return 'network';
    if (msg.includes('http 5') || msg.includes('server')) return 'server';
    if (msg.includes('http 4') || msg.includes('invalid') || msg.includes('required')) return 'validation';
  }
  return 'unknown';
}
